import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import ChevronRight from 'common/Icons/ChevronRight';
import ScorePill from 'components/AiCompetitiveIntelligence/ScorePill';
import GradientBorderButton from 'components/lib/GradientBorderButton';
import { getOrdinalSuffix } from 'components/View/utils';
import { canViewPricingData } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { AICompetitiveIntelligenceType } from 'types/recommendations';

import MarketPositionTable from './MarketPositionTable';

const Container = styled.div`
  border-radius: 10px;
  padding: 30px;
  background-image: linear-gradient(
    73deg,
    var(--market-position-gradient-start),
    var(--market-position-gradient-mid) 57%,
    var(--market-position-gradient-end) 100%
  );
  color: var(--white);
`;

const Header = styled.div`
  display: flex;
  flex-flow: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
  align-items: center;
`;

const ImageContainer = styled.div`
  background-image: linear-gradient(
    315deg,
    var(--competitor-purple),
    var(--aici-action-icon-gradient-mid) 50%,
    var(--flamingo-pink) 0%
  );
  padding: 6px;
  border-radius: 4px;
  width: 20px;
  height: 20px;
`;

const Title = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: var(--white-60);
`;

const Actions = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
`;

const AICIButton = styled(GradientBorderButton)`
  border: none;
  border-radius: 4px;
  box-shadow: none;

  &::before {
    display: none;
  }

  .gradient-border-button-content {
    font-family: var(--font-bold);
    font-size: 14px;
    gap: 8px;
  }
`;

const PricingGridButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 4px;
  background-color: var(--button-blue);
  color: var(--white);
  font-size: 14px;
  padding: 8px 20px;
  font-family: var(--font-bold);
  cursor: pointer;
`;

const Body = styled.div`
  display: flex;
  flex-flow: row;
  gap: 30px;
  margin-top: 20px;
`;

const PositionColumn = styled.div`
  width: 345px;
`;

const OrdinalText = styled.div`
  font-size: 60px;
`;

const OrdinalSuffix = styled.span`
  font-size: 28px;
`;

const ScorePillWrapper = styled.div`
  margin-top: 5px;

  .suffix,
  .score,
  .avg-label {
    color: var(--white);
  }

  .vs-avg {
    color: var(--white-60);
  }

  .track {
    height: 5px;
    background-color: var(--white-60);
    border-radius: 4px;
  }

  .indicator {
    background-color: var(--white);
  }
`;

const Description = styled.div`
  color: var(--white-60);
  font-size: 14px;
  margin-top: 15px;
`;

const ComparisonColumn = styled.div`
  flex: 1;
  display: flex;
  border-radius: 8px;
  background-color: var(--white-10);
`;

type Props = {
  data?: AICompetitiveIntelligenceType;
  communityId?: number;
  competingCommunitiesCount?: number;
  pricingGridViewId?: number;
};

const MarketPositionCard = ({ data, communityId, competingCommunitiesCount, pricingGridViewId }: Props) => {
  const navigate = useNavigate();
  const { currentUser } = useAppSelector((state) => state.auth);
  const showPricingData = canViewPricingData(currentUser);

  const ranking = data?.ranking;
  const overallScore = data?.overall_score;
  const hasOverallScore = typeof overallScore === 'number';
  const marketAverage = data?.competitor_average ?? null;

  const handleOpenAICompetitiveIntelligence = () => {
    navigate(communityId ? `/ai-competitive-intelligence?communityId=${communityId}` : '/ai-competitive-intelligence');
  };

  const handleOpenPricingGrid = () => {
    if (!communityId || !pricingGridViewId) return;
    navigate(`/community/${communityId}/view/${pricingGridViewId}`);
  };

  return (
    <Container data-test-id="market-position-card">
      <Header>
        <TitleGroup>
          <ImageContainer>
            <img src="/icons/stacked-bar-chart-black.svg" width="20" height="20" />
          </ImageContainer>
          <div>
            <Title>Market Position</Title>
            <Subtitle>
              {competingCommunitiesCount
                ? `Position within ${competingCommunitiesCount} competing communities`
                : 'Position within competing communities'}
            </Subtitle>
          </div>
        </TitleGroup>
        <Actions>
          <AICIButton data-test-id="market-position-ai-ci-button" onClick={handleOpenAICompetitiveIntelligence}>
            <img src="/icons/ai-blob.svg" alt="" width={20} height={20} /> AI Competitive Intelligence{' '}
            <ChevronRight fill="var(--flamingo-pink)" width={20} height={20} />
          </AICIButton>
          {showPricingData && (
            <PricingGridButton
              data-test-id="market-position-pricing-grid-button"
              onClick={handleOpenPricingGrid}
              disabled={!pricingGridViewId}
            >
              Market Overview <ChevronRight fill="var(--white)" width={20} height={20} />
            </PricingGridButton>
          )}
        </Actions>
      </Header>
      <Body>
        <PositionColumn>
          <OrdinalText>
            {ranking ? (
              <span>
                {ranking}
                <OrdinalSuffix>{getOrdinalSuffix(ranking).slice(String(ranking).length)}</OrdinalSuffix>
              </span>
            ) : (
              'No data'
            )}
          </OrdinalText>
          {hasOverallScore && (
            <ScorePillWrapper>
              <ScorePill score={overallScore} average={marketAverage} vsAvgLabel="vs market avg" />
            </ScorePillWrapper>
          )}
          <Description>
            The score above is a weighted AI rating across 7 categories, such as reviews, amenities, activities, etc.
            Open AI Competitive Intelligence to view details.
            {showPricingData && ' Open Market Overview to see the classic home screen.'}
          </Description>
        </PositionColumn>
        <ComparisonColumn>
          <MarketPositionTable communityId={communityId} />
        </ComparisonColumn>
      </Body>
    </Container>
  );
};

export default MarketPositionCard;
